/**
 * Dynamic Component Loader Utility - Template-Driven Version
 * Loads and renders agent components using JSON template definitions
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import TemplateRenderer from '../components/TemplateRenderers/TemplateRenderer';

class ComponentLoader {
  constructor() {
    this.componentCache = new Map();
  }

  /**
   * Load component template definition from backend
   */
  async loadComponent(agentId, componentName, token) {
    try {
      // Check cache first
      const cacheKey = `${agentId}-${componentName}`;
      if (this.componentCache.has(cacheKey)) {
        return this.componentCache.get(cacheKey);
      }

      // Fetch component template definition from backend
      const response = await fetch(`/api/agents/${agentId}/frontend/components/${componentName}/definition`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load component: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.definition) {
        throw new Error('Invalid template definition received');
      }

      // Create executable component using template renderer
      const executableComponent = this.createTemplateComponent(data.definition, agentId);
      
      // Cache the result
      const result = {
        component: executableComponent,
        metadata: data.definition.metadata || { name: componentName, displayName: componentName },
        agentId: agentId,
        templateType: data.template_type
      };
      
      this.componentCache.set(cacheKey, result);
      return result;

    } catch (error) {
      console.error(`Error loading component ${componentName}:`, error);
      return {
        component: this.createErrorComponent(componentName, error.message),
        metadata: { name: componentName, type: 'error' },
        agentId: agentId
      };
    }
  }

  /**
   * Create React component from template definition
   */
  createTemplateComponent(definition, agentId) {
    console.log('createTemplateComponent called with:', { definition, agentId });
    
    // Create a wrapper component that will receive props from AgentComponent
    const TemplateWrapper = React.forwardRef((props, ref) => {
      console.log('TemplateWrapper called with props:', props);
      
      // Merge the passed props with the pre-bound definition and agentId
      const mergedProps = {
        definition,
        agentId,
        token: props?.token,
        componentName: props?.componentName,
        onClose: props?.onClose,
        onSubmit: props?.onSubmit,
        workflowContext: props?.workflowContext,
        onNavigate: props?.onNavigate,
        loading: props?.loading
      };
      
      console.log('Merged props for TemplateRenderer:', mergedProps);
      
      return React.createElement(TemplateRenderer, mergedProps);
    });
    
    // Set display name for debugging
    TemplateWrapper.displayName = `TemplateWrapper(${definition?.metadata?.name || 'Unknown'})`;
    
    return TemplateWrapper;
  }

  /**
   * Create error component for failed loads
   */
  createErrorComponent(componentName, errorMessage) {
    return (props) => {
      return React.createElement('div', {
        className: 'p-6 bg-red-50 border border-red-200 rounded-lg text-center'
      }, [
        React.createElement('div', {
          key: 'icon',
          className: 'w-16 h-16 bg-red-500 rounded-full flex items-center justify-center text-white text-2xl mb-4 mx-auto'
        }, React.createElement(FontAwesomeIcon, { icon: faExclamationTriangle })),
        React.createElement('h3', {
          key: 'title',
          className: 'text-lg font-semibold text-red-800 mb-2'
        }, 'Component Load Error'),
        React.createElement('p', {
          key: 'component',
          className: 'text-red-700 mb-2'
        }, `Failed to load component: ${componentName}`),
        React.createElement('p', {
          key: 'error',
          className: 'text-red-600 text-sm'
        }, errorMessage)
      ]);
    };
  }

  /**
   * Clear component cache
   */
  clearCache() {
    this.componentCache.clear();
  }

  /**
   * Get cached component info
   */
  getCacheInfo() {
    return Array.from(this.componentCache.keys());
  }
}

// Create singleton instance
export const componentLoader = new ComponentLoader();

// Export utility functions
export const loadAgentComponent = (agentId, componentName, token) => {
  return componentLoader.loadComponent(agentId, componentName, token);
};

export const clearComponentCache = () => {
  return componentLoader.clearCache();
};

export default componentLoader;